if (!customElements.get('product-inline-zoom')) {
  const DEFAULT_MAX_SCALE = 2.8;
  const SCALE_CEILING = 6;
  const TAP_MOVE_TOLERANCE = 10;
  const TAP_MAX_DURATION = 250;
  const PINCH_RESET_THRESHOLD = 1.05;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  customElements.define(
    'product-inline-zoom',
    class ProductInlineZoom extends HTMLElement {
      scale = 1;
      offsetX = 0;
      offsetY = 0;
      lastTouchAt = 0;
      pinching = false;
      pinchStartDistance = 1;
      pinchStartScale = 1;

      constructor() {
        super();
        this.desktop = window.matchMedia('(min-width: 750px)');
        this.maxScale = DEFAULT_MAX_SCALE;
      }

      // Set by the section's `mobile_zoom_scale` setting; falls back when this
      // snippet is rendered from a section that doesn't expose it. Read on connect
      // rather than in the constructor, where attributes aren't guaranteed present.
      resolveMaxScale() {
        const configured = parseFloat(this.dataset.maxScale);
        if (!Number.isFinite(configured) || configured <= 1) return DEFAULT_MAX_SCALE;
        return Math.min(configured, SCALE_CEILING);
      }

      connectedCallback() {
        this.maxScale = this.resolveMaxScale();
        this.frame = this.querySelector('.product__media');
        this.image = this.frame?.querySelector('img');
        if (!this.image) return;

        this.toggle = this.querySelector('.product__media-toggle');
        // No dialog opens on mobile, so the popup affordance would be a lie to AT.
        if (!this.desktop.matches) this.toggle?.removeAttribute('aria-haspopup');

        this.addEventListener('touchstart', this.onTouchStart, { passive: false });
        this.addEventListener('touchmove', this.onTouchMove, { passive: false });
        this.addEventListener('touchend', this.onTouchEnd);
        this.addEventListener('touchcancel', this.onTouchEnd);
        this.addEventListener('click', this.onClick, true);
        // Safari resolves pinches to its own page zoom unless the gesture is claimed here.
        this.addEventListener('gesturestart', this.preventDefault);
        this.addEventListener('gesturechange', this.preventDefault);

        this.desktop.addEventListener('change', this.onBreakpointChange);
        this.closest('slider-component')?.addEventListener('slideChanged', this.reset);
      }

      disconnectedCallback() {
        this.desktop.removeEventListener('change', this.onBreakpointChange);
      }

      get enabled() {
        return !this.desktop.matches;
      }

      get zoomed() {
        return this.scale > 1.01;
      }

      preventDefault = (event) => {
        if (this.enabled) event.preventDefault();
      };

      onBreakpointChange = () => {
        this.reset();
        if (this.desktop.matches) this.toggle?.setAttribute('aria-haspopup', 'dialog');
        else this.toggle?.removeAttribute('aria-haspopup');
      };

      onTouchStart = (event) => {
        if (!this.enabled) return;
        this.lastTouchAt = Date.now();

        if (event.touches.length === 2) {
          event.preventDefault();
          this.pinching = true;
          this.tap = null;
          this.pinchStartDistance = this.distanceBetween(event.touches);
          this.pinchStartScale = this.scale;
          this.classList.add('is-gesturing');
          return;
        }

        if (event.touches.length !== 1) return;
        const touch = event.touches[0];
        this.tap = { x: touch.clientX, y: touch.clientY, at: Date.now(), moved: false };
        if (this.zoomed) this.startPan(touch);
      };

      onTouchMove = (event) => {
        if (!this.enabled) return;

        if (this.pinching && event.touches.length === 2) {
          event.preventDefault();
          const ratio = this.distanceBetween(event.touches) / this.pinchStartDistance;
          const focal = this.toLocalPoint(this.midpointOf(event.touches));
          this.zoomTo(this.pinchStartScale * ratio, focal.x, focal.y);
          return;
        }

        const touch = event.touches[0];
        if (!touch) return;

        if (this.tap && Math.hypot(touch.clientX - this.tap.x, touch.clientY - this.tap.y) > TAP_MOVE_TOLERANCE) {
          this.tap.moved = true;
        }

        if (this.pan && this.zoomed) {
          event.preventDefault();
          this.setOffset(
            this.pan.offsetX + touch.clientX - this.pan.x,
            this.pan.offsetY + touch.clientY - this.pan.y
          );
        }
      };

      onTouchEnd = (event) => {
        if (!this.enabled) return;
        this.lastTouchAt = Date.now();

        if (this.pinching) {
          if (event.touches.length >= 2) return;
          this.pinching = false;
          this.pan = null;
          this.classList.remove('is-gesturing');
          if (this.scale < PINCH_RESET_THRESHOLD) this.reset();
          else if (event.touches.length === 1) this.startPan(event.touches[0]);
          return;
        }

        if (event.touches.length === 0) {
          this.pan = null;
          this.classList.remove('is-gesturing');
        }

        const tap = this.tap;
        this.tap = null;
        if (!tap || tap.moved || Date.now() - tap.at > TAP_MAX_DURATION) return;
        this.toggleZoomAt(tap.x, tap.y);
      };

      onClick = (event) => {
        if (!this.enabled) return;
        // Touch already resolved this gesture; stop the tap from reaching the lightbox opener.
        event.preventDefault();
        event.stopPropagation();
        if (Date.now() - this.lastTouchAt < 500) return;
        this.toggleZoomAt(event.clientX, event.clientY);
      };

      startPan(touch) {
        this.pan = { x: touch.clientX, y: touch.clientY, offsetX: this.offsetX, offsetY: this.offsetY };
        this.classList.add('is-gesturing');
      }

      toggleZoomAt(clientX, clientY) {
        if (this.zoomed) {
          this.reset();
          return;
        }
        const point = this.toLocalPoint({ clientX, clientY });
        this.zoomTo(this.maxScale, point.x, point.y);
      }

      distanceBetween(touches) {
        return Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
      }

      midpointOf(touches) {
        return {
          clientX: (touches[0].clientX + touches[1].clientX) / 2,
          clientY: (touches[0].clientY + touches[1].clientY) / 2,
        };
      }

      toLocalPoint(point) {
        const rect = this.frame.getBoundingClientRect();
        return { x: point.clientX - rect.left, y: point.clientY - rect.top };
      }

      // Keeps the focal point pinned while the scale changes, so the image grows
      // out of wherever the finger is rather than jumping to the centre.
      zoomTo(scale, focalX, focalY) {
        const next = clamp(scale, 1, this.maxScale);
        const centredX = focalX - this.frame.offsetWidth / 2;
        const centredY = focalY - this.frame.offsetHeight / 2;
        const anchorX = (centredX - this.offsetX) / this.scale;
        const anchorY = (centredY - this.offsetY) / this.scale;

        this.scale = next;
        this.setOffset(centredX - anchorX * next, centredY - anchorY * next);
      }

      setOffset(x, y) {
        const maxX = ((this.scale - 1) * this.frame.offsetWidth) / 2;
        const maxY = ((this.scale - 1) * this.frame.offsetHeight) / 2;
        this.offsetX = clamp(x, -maxX, maxX);
        this.offsetY = clamp(y, -maxY, maxY);
        this.render();
      }

      reset = () => {
        if (this.scale === 1 && this.offsetX === 0 && this.offsetY === 0) return;
        this.scale = 1;
        this.offsetX = 0;
        this.offsetY = 0;
        this.render();
      };

      render() {
        this.image.style.transform = `translate3d(${this.offsetX}px, ${this.offsetY}px, 0) scale(${this.scale})`;
        this.classList.toggle('is-zoomed', this.zoomed);
      }
    }
  );
}
